import { ApiResponse } from '../types';

export interface CacheConfig {
  ttl: number; // Time to live in milliseconds
  maxSize: number; // Maximum number of items
  enablePersistence: boolean; // Whether to persist to localStorage
}

export interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
  key: string;
}

export interface CacheStats {
  hits: number;
  misses: number;
  size: number;
  hitRate: number;
}

class CacheService {
  private cache = new Map<string, CacheItem<any>>();
  private stats: CacheStats = { hits: 0, misses: 0, size: 0, hitRate: 0 };
  private config: CacheConfig;
  private cleanupInterval: NodeJS.Timeout | null = null;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      ttl: 5 * 60 * 1000, // 5 minutes default
      maxSize: 100,
      enablePersistence: true,
      ...config
    };

    this.loadFromPersistence();
    this.startCleanupInterval();
  }

  /**
   * Get item from cache
   */
  get<T>(key: string): T | null {
    const item = this.cache.get(key);
    
    if (!item) {
      this.stats.misses++;
      this.updateHitRate();
      return null;
    }

    // Check if item has expired
    if (Date.now() - item.timestamp > item.ttl) {
      this.cache.delete(key);
      this.stats.misses++;
      this.updateHitRate();
      return null;
    }

    this.stats.hits++;
    this.updateHitRate();
    return item.data;
  }

  /**
   * Set item in cache
   */
  set<T>(key: string, data: T, ttl?: number): void {
    // Remove oldest items if cache is full
    if (this.cache.size >= this.config.maxSize) {
      this.evictOldest();
    }

    const item: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.config.ttl,
      key
    };

    this.cache.set(key, item);
    this.stats.size = this.cache.size;

    if (this.config.enablePersistence) {
      this.persistItem(key, item);
    }
  }

  /**
   * Remove item from cache
   */
  delete(key: string): boolean {
    const deleted = this.cache.delete(key);
    this.stats.size = this.cache.size;
    
    if (this.config.enablePersistence) {
      localStorage.removeItem(`cache_${key}`);
    }
    
    return deleted;
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
    this.stats = { hits: 0, misses: 0, size: 0, hitRate: 0 };
    
    if (this.config.enablePersistence) {
      this.clearPersistence();
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    return { ...this.stats };
  }

  /**
   * Check if key exists and is not expired
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Get all cache keys
   */
  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Create cache key for API requests
   */
  createApiKey(url: string, params?: Record<string, any>): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `api_${url}_${paramString}`;
  }

  /**
   * Cache API response with intelligent TTL based on content type
   */
  cacheApiResponse<T>(key: string, response: ApiResponse<T>): void {
    let ttl = this.config.ttl;

    // Adjust TTL based on content type
    if (key.includes('vocabulary') || key.includes('grammar')) {
      ttl = 30 * 60 * 1000; // 30 minutes for content
    } else if (key.includes('dashboard') || key.includes('stats')) {
      ttl = 2 * 60 * 1000; // 2 minutes for dynamic data
    } else if (key.includes('profile') || key.includes('auth')) {
      ttl = 10 * 60 * 1000; // 10 minutes for user data
    }

    this.set(key, response, ttl);
  }

  private updateHitRate(): void {
    const total = this.stats.hits + this.stats.misses;
    this.stats.hitRate = total > 0 ? this.stats.hits / total : 0;
  }

  private evictOldest(): void {
    let oldestKey = '';
    let oldestTime = Date.now();

    for (const [key, item] of this.cache.entries()) {
      if (item.timestamp < oldestTime) {
        oldestTime = item.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.delete(oldestKey);
    }
  }

  private startCleanupInterval(): void {
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, 60 * 1000); // Cleanup every minute
  }

  private cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    for (const [key, item] of this.cache.entries()) {
      if (now - item.timestamp > item.ttl) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.delete(key));
  }

  private persistItem<T>(key: string, item: CacheItem<T>): void {
    try {
      localStorage.setItem(`cache_${key}`, JSON.stringify(item));
    } catch (error) {
      console.warn('Failed to persist cache item:', error);
    }
  }

  private loadFromPersistence(): void {
    if (!this.config.enablePersistence) return;

    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('cache_')) {
          const cacheKey = key.replace('cache_', '');
          const itemData = localStorage.getItem(key);
          
          if (itemData) {
            const item: CacheItem<any> = JSON.parse(itemData);
            
            // Check if item is still valid
            if (Date.now() - item.timestamp <= item.ttl) {
              this.cache.set(cacheKey, item);
            } else {
              localStorage.removeItem(key);
            }
          }
        }
      }
      
      this.stats.size = this.cache.size;
    } catch (error) {
      console.warn('Failed to load cache from persistence:', error);
    }
  }

  private clearPersistence(): void {
    try {
      const keysToRemove: string[] = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith('cache_')) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.warn('Failed to clear cache persistence:', error);
    }
  }

  /**
   * Destroy cache service and cleanup
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.clear();
  }
}

// Create singleton instance
export const cacheService = new CacheService({
  ttl: 5 * 60 * 1000, // 5 minutes
  maxSize: 200,
  enablePersistence: true
});

// Admin-specific cache configuration
export const adminCacheService = new CacheService({
  ttl: 2 * 60 * 1000, // 2 minutes for admin data
  maxSize: 50,
  enablePersistence: false // Admin data should not persist across sessions
});

export default CacheService;